/**
 * ToonRenderer: the approved art-test look on live sim state.
 *  - toon materials (style.ts), hull outlines, UnrealBloom + colour grade + Neutral tone mapping (post.ts)
 *  - KayKit dungeon dressing (dungeon.ts), VRM heroes and KayKit mobs (units.ts)
 *  - telegraph decals (areas.ts) and spell FX (fx.ts), selection ring and target marker
 * Assets are loaded once (assets.ts) and passed in; this renderer only owns per-run objects.
 */
import * as THREE from 'three';
import type { ContentRegistry, DungeonDef, GatherProfession, UnitId, Vec2 } from '@keystone/sim';
import type { CameraRig } from '../camera';
import { perf } from '../../debug/perf';
import { DEFAULT_SETTINGS, type GraphicsSettings, qualityProfile } from '../settings';
import type { Renderer, RenderFrame, RenderUnit, ScreenPoint } from '../types';
import { AreaDecal } from './areas';
import { MechanicsFx } from './fx/mechanics/view'; // +encounters
import type { ToonAssets } from './assets';
import { type DungeonBuild, buildToonDungeon } from './dungeon';
import { groundY, projectGround, rayToGround, setGroundForDungeon, smoothHeight } from './ground';
import { GENERIC_THEME } from './themes/index';
import type { DungeonTheme } from './themes/types';
import { type FxView, ToonFx } from './fx';
import { type HullRegistry, U, setShadowHue } from './style';
import { type GradeUniforms, ToonPost } from './post';
import { type ClipFxHooks, UnitShared, type UnitVisual, createUnitView } from './units';
import { stabilizePrograms } from './programs';
import { SHADOW_LAYER } from './shadowProxy';
import { WorldLootView } from './world/view'; // +world-resources
import { PlayerGroundCues } from './placement'; // +castux: ground placement circle + cast ring
import { combatAudio } from '../../audio/engine'; // +impact
import { ambience } from '../../audio/ambience'; // +audio: room tones, footstep surfaces
import { music } from '../../audio/music'; // +music: open-world background music
import { AfterImages } from './feel/afterimage'; // +impact
import { FeelDirector } from './feel/director'; // +impact
import { Footsteps } from './feel/footsteps'; // +controls
import { CamCollision, boomGroundLimit } from './camCollision'; // +camera
import { SelfFade, blocksView, capsuleDistance, fadeTarget } from './selfFade'; // +camera
import { EYE_FLOOR_CLEARANCE, collideDistance } from '../camera'; // +camera
import type { NpcMarker } from '../types'; // +field
import type { FieldBuild } from './outdoor/field'; // +field
import { type SkywaysView, buildSkyways, nearestChute } from './outdoor/skyways'; // +glide
import { WindStreaks } from './fx/windStreaks'; // +glide
import { GlideAudio, chuteLevel, windLevel } from '../../audio/glideAudio'; // +glide
import type { AirspaceDef } from '@keystone/sim'; // +glide
import { TownNpcs } from './outdoor/npcs'; // +field
import { GatherNodesView } from './outdoor/nodes'; // +prof
import { registerGatherClips } from './anim/gather'; // +prof
import { type CelebrationSize, Celebrations, levelUpSize } from './fx/celebrate'; // +field
import { EMOTE_CHEER, EMOTE_LEVELUP, registerEmotes } from './anim/emotes'; // +field
import { captureHook } from '../../debug/captureHook'; // +capture
import { OUTDOOR_FAR, atmosphere } from './outdoor/atmosphere'; // +atmos: time of day, sky, far world

export interface ToonRendererOptions {
  assets: ToonAssets;
  content: ContentRegistry;
  settings?: GraphicsSettings;
}

export interface ToonStats {
  fps: number;
  drawCalls: number;
  triangles: number;
  units: number;
  visibleUnits: number;
  /** CPU ms spent in render() (EMA). */
  cpuMs: number;
  /** Compiled GPU programs (a jump during play = a shader compile hitch). */
  programs?: number;
}

const KEY_DIR = new THREE.Vector3(-0.55, 1.0, 0.62).normalize();
/** +zone: units farther than this from the camera are not built / drawn (big open maps; fog covers it). */
const UNIT_FAR_YD = 115;
const FRIEND_RING = new THREE.Color(0.35, 1.35, 1.0);
const FOE_RING = new THREE.Color(1.6, 0.4, 0.25);
const BG = new THREE.Color(0x0a0b12);
/** +impact: dodge afterimage tint (cool, moderate: translucent, low opacity). */
const GHOST_COLOR = [0.42, 0.6, 0.95] as const;
const BASE_FOV = 42;
/** +camera: head height of a hero view as a fraction of its drawn height (VRM head bone, measured). */
const HEAD_FRAC = 0.89; // head bone ≈ 2.05 yd on a 2.3 yd hero
/** +camera: keep the eye this far in front of a wall (near plane is 0.5). */
const CAM_CLEAR = 0.5;
/** +camera: half-width of the side collision rays at the eye. */
const CAM_SIDE = 0.35;

/** +jump: vertical camera lag behind the hop (ms). */
const JUMP_CAM_TAU_MS = 70;
/** +glide: the camera eases after a flyer more softly (ms) and pulls back this much (× distance) while the glider is open. */
const GLIDE_CAM_TAU_MS = 180;
const GLIDE_CAM_PULL = 0.32;
const GLIDE_CAM_EASE_MS = 700;

/** +presence: stop a view's meshes casting into the shadow map; returns them (to restore). */
function noShadowCast(root: THREE.Object3D): THREE.Mesh[] {
  const out: THREE.Mesh[] = [];
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (m.isMesh && m.castShadow) {
      m.castShadow = false;
      out.push(m);
    }
  });
  return out;
}

export function createToonRenderer(container: HTMLElement, opts: ToonRendererOptions): Renderer & { stats(): ToonStats } {
  const { assets, content } = opts;
  let settings: GraphicsSettings = { ...(opts.settings ?? DEFAULT_SETTINGS) };
  let profile = qualityProfile(settings.quality);

  // `?lowlatency=1`: a desynchronized (low-latency) WebGL canvas. Chrome then presents the canvas
  // without waiting for the compositor (≈1 frame less input→photon on the 3D view), but the DOM
  // HUD stays on the compositor path, so nameplates / combat text can trail the 3D by a frame and
  // the canvas may tear. Off by default; see the perf report.
  const lowLatency = typeof location !== 'undefined' && new URLSearchParams(location.search).get('lowlatency') === '1';
  let gl: THREE.WebGLRenderer;
  if (lowLatency) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('webgl2', { desynchronized: true, antialias: false, alpha: false, powerPreference: 'high-performance', stencil: true, depth: true, preserveDrawingBuffer: false } as WebGLContextAttributes);
    gl = ctx ? new THREE.WebGLRenderer({ canvas, context: ctx }) : new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    console.info(`[toon] low-latency canvas: desynchronized=${(ctx?.getContextAttributes() as { desynchronized?: boolean } | null)?.desynchronized ?? false}`);
  } else gl = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
  gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, profile.pixelRatioCap, 2));
  gl.shadowMap.enabled = profile.shadows;
  gl.shadowMap.type = THREE.PCFShadowMap;
  gl.toneMapping = THREE.NeutralToneMapping;
  gl.toneMappingExposure = 1.0;
  gl.domElement.style.display = 'block';
  gl.info.autoReset = false; // count draw calls across all post passes
  container.appendChild(gl.domElement);

  const scene = new THREE.Scene();
  scene.background = BG;
  const camera = new THREE.PerspectiveCamera(BASE_FOV, 1, 0.5, 400);

  // Lights: warm key with shadows (follows the camera focus), soft cool hemisphere.
  const key = new THREE.DirectionalLight(0xffffff, 3.2);
  key.castShadow = true;
  key.shadow.mapSize.set(profile.shadowMapSize, profile.shadowMapSize);
  Object.assign(key.shadow.camera, { left: -28, right: 28, top: 28, bottom: -28, near: 5, far: 110 });
  key.shadow.bias = -0.0005;
  key.shadow.normalBias = 0.035;
  scene.add(key, key.target);
  U.uKeyIntensity.value = key.intensity / Math.PI;
  const hemi = new THREE.HemisphereLight(0xcfe0ff, 0x3a3f4c, 0.5);
  scene.add(hemi);

  // Post: scene (MSAA) → bloom → one final pass: bloom add + grade + tone map + sRGB (post.ts).
  const grade: GradeUniforms = {
    uSat: { value: 1.12 },
    uGain: { value: new THREE.Vector3(1.03, 1.0, 0.97) },
    // Cool blue-grey lift (less red than the art test's) so the dark flagstones read stone-blue, not purple.
    uLift: { value: new THREE.Vector3(0.006, 0.009, 0.024) },
    uVig: { value: 0.28 },
    // +impact: impact-frame contrast punch (big crits / finishers) and the screen-edge hurt flash.
    uPunch: { value: 0 },
    uHurt: { value: 0 },
  };
  let size = { w: Math.max(1, container.clientWidth || window.innerWidth), h: Math.max(1, container.clientHeight || window.innerHeight) };
  const post = new ToonPost(gl, scene, camera, profile.msaa, grade);
  post.setSize(size.w, size.h, gl.getPixelRatio());
  post.bloomEnabled = settings.bloom && profile.bloomAllowed;
  const bloom = post.bloom;
  atmosphere.onNight = (night) => ambience.setTimeOfDay(night ? 'night' : 'day'); // +audio +atmos: night beds follow the time of day

  // The shadow pass also sees SHADOW_LAYER (merged shadow proxies, see shadowProxy.ts): three.js
  // tests shadow casters against the *main* camera's layers, after the main render list was
  // built, so the layer is enabled only while the shadow map renders. Plus a profiler span.
  const shadowRender = gl.shadowMap.render.bind(gl.shadowMap);
  gl.shadowMap.render = (lights, sc, cam) => {
    perf.begin('r.gl.shadow');
    cam.layers.enable(SHADOW_LAYER);
    try {
      shadowRender(lights, sc, cam);
    } finally {
      cam.layers.disable(SHADOW_LAYER);
      perf.end('r.gl.shadow');
    }
  };

  const hulls: HullRegistry = { unit: new Set(), prop: new Set() };
  const shared = new UnitShared();
  const fx = new ToonFx(content);
  // Dev-only handle for inspecting the VFX director from the console.
  const shakeOff = { x: 0, y: 0 };
  const camRight = new THREE.Vector3();
  const camUp = new THREE.Vector3();
  const camSaved = new THREE.Vector3();
  const fxView: FxView = {
    units: new Map(),
    heightOf: (id) => units.get(id)?.height ?? 2,
    handOf: (id, out) => {
      const v = units.get(id);
      return v && v.root.visible ? v.handPos(out) : null;
    },
    camera,
    playerId: null,
    focus: { x: 0, y: 0 },
  };
  // Dev-only handles for inspecting / poking the VFX director from the console.
  if (import.meta.env.DEV) Object.assign(globalThis as object, { __toonFx: fx, __toonFxView: fxView, __toonGl: { gl, scene, camera } });
  scene.add(fx.group);
  // +impact: the feel layer (render/toon/feel): sound, unit reactions, camera kick, afterimages.
  const afterimages = new AfterImages();
  scene.add(afterimages.group);
  const feel = new FeelDirector(combatAudio, content, {
    hitStop: (ms) => void fx.hitStop.trigger(ms),
    shake: (t) => fx.shake.add(t),
    react: (id, flinch, flash, dx, dz, color) => units.get(id)?.impact?.(flinch, flash, dx, dz, color),
    procSpark: (id, color) => fx.procSpark(id, fxView, color),
    afterimage: (id) => {
      const src = units.get(id)?.ghostSource?.();
      if (src) afterimages.spawn(src, GHOST_COLOR);
    },
  });
  fx.onLand = (abilityId, spec, sourceId, hit, parried, view) => feel.land(abilityId, spec, sourceId, hit, parried, view);
  fx.contactIn = (id, abilityId) => units.get(id)?.contactIn?.(abilityId) ?? -1;
  fx.onDeferDeath = (id, s) => units.get(id)?.deferDeath?.(s);
  fx.onReleaseDeath = (id) => units.get(id)?.releaseDeath?.();
  combatAudio.install();
  combatAudio.preload('combat'); // +audio: combat sprite loads with the run
  if (import.meta.env.DEV) Object.assign(globalThis as object, { __toonFeel: feel, __combatAudio: combatAudio });
  scene.add(shared.blobs);
  scene.add(shared.trails.group);
  let dungeon: DungeonBuild | null = null;
  const units = new Map<UnitId, UnitVisual>();
  const areas = new Map<string, AreaDecal>();
  // +encounters: enemy-mechanic overlays (soak pips, beams, tethers, debuff rings, shield bubbles).
  const mechFx = new MechanicsFx();
  scene.add(mechFx.group);
  const worldLoot = new WorldLootView(assets, hulls, (a) => fx.shake.add(a)); // +world-resources
  scene.add(worldLoot.group); // +world-resources
  const groundCues = new PlayerGroundCues(); // +castux
  scene.add(groundCues.group); // +castux
  const unitHeight = (id: UnitId): number => units.get(id)?.height ?? 2;
  // +field: town NPCs, celebrations (level-up pillar), spawn rise-in / despawn sink, elite rings.
  registerEmotes();
  let townNpcs: TownNpcs | null = null;
  let gatherNodes: GatherNodesView | null = null; // +prof
  let gatherTracking: GatherProfession | null = null; // +prof
  let npcMarkers: Record<string, NpcMarker | null> = {};
  const celebrations = new Celebrations(shared.glowTex);
  scene.add(celebrations.group);
  const baseBloom = bloom.strength;
  const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  /** Seconds since init (the initial field fill doesn't rise in). */
  let sinceInit = 0;
  const rising = new Map<UnitId, number>();
  const dying: { v: UnitVisual; t: number }[] = [];
  const eliteRings = new Map<UnitId, THREE.Mesh>();
  const eliteGeo = new THREE.RingGeometry(0.9, 1.0, 48).rotateX(-Math.PI / 2);
  const eliteMat = new THREE.MeshBasicMaterial({ color: new THREE.Color(1.0, 0.78, 0.3), transparent: true, opacity: 0.85, depthWrite: false, blending: THREE.AdditiveBlending, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const eliteInner = new THREE.RingGeometry(0.78, 0.82, 6).rotateX(-Math.PI / 2);
  const RISE_S = 0.75;
  const SINK_S = 1.1;
  const ELITE_SCALE = 1.15;
  const celebrateAt = new THREE.Vector3();
  const followV = new THREE.Vector3();
  if (import.meta.env.DEV) Object.assign(globalThis as object, { __toonCelebrate: (size: CelebrationSize = 'big', id?: UnitId) => celebrateUnit(id ?? fxView.playerId ?? '', size, true) });

  /** +field: start a celebration on unit `id` (follows it); `emote` plays the hero's cheer. */
  function celebrateUnit(id: UnitId, size: CelebrationSize, emote: boolean): void {
    const u = lastFrame?.units.find((x) => x.id === id);
    const v = units.get(id);
    if (!u && !v) return;
    if (v) celebrateAt.copy(v.root.position);
    else celebrateAt.set(u!.pos.x, groundY(u!.pos.x, u!.pos.y), -u!.pos.y);
    celebrations.spawn(size, id, celebrateAt, id === fxView.playerId);
    if (emote) v?.cue('spell', size === 'small' ? EMOTE_CHEER : EMOTE_LEVELUP);
  }
  celebrations.onBurst = (size, mine, at) => {
    const cam = shakeAllowed();
    if (mine && cam && size !== 'small') {
      fx.hitStop.trigger(size === 'huge' ? 170 : 140);
      fx.shake.add(size === 'huge' ? 0.42 : 0.3);
    }
    // The burst sound: full for your own ding, quieter / darker for someone else's.
    const d = Math.hypot(at.x - camera.position.x, at.z - camera.position.z);
    const far = mine ? 0 : Math.min(1, d / 60);
    if (size !== 'small') combatAudio.play('level.impact', { gain: mine ? 1 : 0.45 * (1 - far * 0.7), own: mine, prioBoost: mine ? 1 : 0, far, pitch: size === 'huge' ? 0.92 : 1 });
  };
  // +basekits: Wandering Star's end bloom has its own sound (quieter when far from the camera).
  fx.onStarBurst = (x, y) => {
    const d = Math.hypot(x - camera.position.x, -y - camera.position.z);
    const far = Math.min(1, d / 60);
    combatAudio.play('star.burst', { gain: 1 - far * 0.7, far });
  };
  /** Camera motion (punch, hit-stop, shake) is allowed: the setting is on and the OS doesn't ask for reduced motion. */
  function shakeAllowed(): boolean {
    return settings.screenShake && !reducedMotion;
  }

  // Selection / hover rings and a bobbing target marker.
  const ringGeo = new THREE.RingGeometry(0.86, 1, 48).rotateX(-Math.PI / 2);
  const ringMat = (opacity: number): THREE.MeshBasicMaterial =>
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -4, polygonOffsetUnits: -4 });
  // Target: a soft band plus a crisp thin outer edge, and a quick "snap in" pulse when the
  // target changes (scale 1.4 → 1 over ~0.2 s, edge flash) so Tab reads instantly.
  const selRing = new THREE.Mesh(ringGeo, ringMat(0.5));
  const edgeGeo = new THREE.RingGeometry(0.955, 1.0, 72).rotateX(-Math.PI / 2);
  const selEdgeMat = ringMat(1);
  const selEdge = new THREE.Mesh(edgeGeo, selEdgeMat);
  selEdge.renderOrder = 6;
  selRing.add(selEdge);
  const hovRing = new THREE.Mesh(ringGeo, ringMat(0.35));
  selRing.renderOrder = hovRing.renderOrder = 5;
  let selId: UnitId | null = null;
  let selAge = 1;
  const SEL_PULSE_S = 0.2;
  const markerGeo = new THREE.ConeGeometry(0.22, 0.45, 4).rotateX(Math.PI);
  const markerMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const marker = new THREE.Mesh(markerGeo, markerMat);
  scene.add(selRing, hovRing, marker);
  selRing.visible = hovRing.visible = marker.visible = false;

  const raycaster = new THREE.Raycaster();
  const pickList: THREE.Object3D[] = [];
  const tmp = new THREE.Vector3();
  const projV = new THREE.Vector3();
  let lastFrame: RenderFrame | null = null;
  // +camera: collision blockers (per dungeon), the eased collided distance, base FOV, self-fade.
  let camCol: CamCollision | null = null;
  let camDist = NaN;
  let baseFov = BASE_FOV;
  const camPivot = new THREE.Vector3();
  const selfFade = new SelfFade();
  if (import.meta.env.DEV) Object.assign(globalThis as object, { __toonCam: { col: () => camCol, dist: () => camDist, fade: selfFade } }); // +camera: dev probe
  /** Smoothed floor height under the camera focus. */
  let camGround = NaN;
  /** +jump: the followed hero's drawn jump height, lightly smoothed (the camera rises and falls with the hop). */
  let camJump = 0;
  /** +glide: 0..1, eased toward 1 while the followed hero glides (the boom pulls back). */
  let camGlide = 0;
  /** +glide: the open map's chutes and platforms, the wind streaks, the glide wind / chute hum. */
  let skyways: SkywaysView | null = null;
  let airspace: AirspaceDef | null = null;
  const windStreaks = new WindStreaks();
  scene.add(windStreaks.mesh);
  const glideAudio = new GlideAudio();
  const glideAt = new THREE.Vector3();
  const glideVel = new THREE.Vector3();
  const lastFlight = { x: NaN, y: NaN, z: NaN };
  let disposed = false;
  let fpsAcc = 0;
  let fpsFrames = 0;
  let fps = 0;
  let cpuMs = 0;

  // Canvas rect, cached: getBoundingClientRect forces a synchronous layout whenever the DOM
  // changed since the last one, and project() runs once per nameplate / floater per frame
  // right after those elements were moved. Refreshed on resize (the canvas never scrolls).
  let rect: { left: number; top: number; width: number; height: number } | null = null;
  const canvasRect = (): { left: number; top: number; width: number; height: number } => {
    if (!rect) {
      const r = gl.domElement.getBoundingClientRect();
      rect = { left: r.left, top: r.top, width: r.width || 1, height: r.height || 1 };
    }
    return rect;
  };
  const ndcV = new THREE.Vector2();
  const ndc = (sx: number, sy: number): THREE.Vector2 => {
    const r = canvasRect();
    return ndcV.set(((sx - r.left) / r.width) * 2 - 1, -((sy - r.top) / r.height) * 2 + 1);
  };

  // Ability clips (render/toon/anim): fx markers for everyone, hit-stop / shake only for the
  // local player's own clips (others' would stutter the screen).
  const clipFx: ClipFxHooks = {
    cue: (id, kind, p, dir, color, scale) => fx.clipCue(kind, p, dir, color, scale, id === fxView.playerId),
    hitStop: (id, ms, shake) => {
      if (id !== fxView.playerId) return;
      if (fx.hitStop.trigger(ms) && shake > 0) fx.shake.add(shake);
    },
  };
  // Heroes whose clip draws a real blade trail skip the synthetic swing arc.
  fx.ownsSwing = (id) => units.get(id)?.ownsSwing?.() ?? false;
  // +controls: footsteps on run-cycle foot plants (and a tiny dust puff when running fast).
  const DUST_RGB: [number, number, number] = [0.6, 0.55, 0.48];
  const CLOTH_RGB: [number, number, number] = [0.95, 0.9, 0.8]; // +glide
  const stepDir = new THREE.Vector3(1, 0, 0);
  // +presence: waystone travel poof (stars, a ring and a puff of dust in the waystone's rune blue).
  const WAYSTONE_RGB: [number, number, number] = [0.62, 0.8, 1];
  const poofAt = new THREE.Vector3();
  const waystonePoof = (x: number, y: number, mine: boolean): void => {
    poofAt.set(x, groundY(x, y) + 1.0, -y);
    fx.clipCue('holy', poofAt, stepDir, WAYSTONE_RGB, 1.6, mine);
    fx.clipCue('dust', poofAt, stepDir, DUST_RGB, 0.9, mine);
  };
  const footsteps = new Footsteps(combatAudio, (at, mine) => fx.clipCue('dust', at, stepDir, DUST_RGB, 0.22, mine));
  const stepAt = { x: 0, y: 0 }; // +audio
  footsteps.surfaceAt = (x, y) => {
    stepAt.x = x;
    stepAt.y = y;
    return ambience.surfaceAt(stepAt);
  };
  const unitCtx = {
    assets,
    hulls,
    shared,
    npcDef: (id: string) => content.npc(id),
    mobShadows: profile.mobShadows,
    springHz: profile.springHz,
    clipFx,
    footstep: (id: UnitId, foot: 1 | 2, at: THREE.Vector3, speed: number, height: number) => void footsteps.plant(id === fxView.playerId, foot, at, speed, id, height), // +controls
  };

  function applyOutlineVisibility(): void {
    for (const h of hulls.unit) h.visible = settings.outlines;
    for (const h of hulls.prop) h.visible = settings.outlines && profile.propOutlines;
  }

  const frustum = new THREE.Frustum();
  const projScreen = new THREE.Matrix4();
  const sphere = new THREE.Sphere();
  /** +presence: other players' views: meshes whose shadow-map casting was turned off, and skipped animation time. */
  const remoteShadow = new Map<UnitId, THREE.Mesh[]>();
  const remoteDt = new Map<UnitId, number>();
  const remoteHulls = new Map<UnitId, THREE.Object3D[]>();
  let remoteFrame = 0;
  function syncUnits(frame: RenderFrame, dtS: number): Map<UnitId, RenderUnit> {
    remoteFrame++; // +presence
    frustum.setFromProjectionMatrix(new THREE.Matrix4().multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    const byId = new Map<UnitId, RenderUnit>();
    for (const u of frame.units) {
      byId.set(u.id, u);
      let v = units.get(u.id);
      // +zone: units far from the camera (big open maps) are neither built nor drawn.
      const far = (u.pos.x - camera.position.x) ** 2 + (-u.pos.y - camera.position.z) ** 2 > UNIT_FAR_YD * UNIT_FAR_YD && u.id !== frame.playerId;
      if (!v && far) continue;
      if (v && far) {
        v.root.visible = false;
        if (v.root.parent === scene) scene.remove(v.root);
        continue;
      }
      if (!v) {
        v = createUnitView(u, unitCtx);
        if (u.remote) {
          // +presence: blob shadow only; outline hulls kept for toggling (far ones drop them).
          remoteShadow.set(u.id, noShadowCast(v.root));
          const hs: THREE.Object3D[] = [];
          v.root.traverse((o) => {
            if (hulls.unit.has(o)) hs.push(o);
          });
          remoteHulls.set(u.id, hs);
        }
        stabilizePrograms(v.root, { surfaces: true });
        units.set(u.id, v);
        scene.add(v.root);
        applyOutlineVisibility();
      }
      // Off-screen units are hidden and skip animation work (big win: whole-dungeon pack lists).
      const gy = groundY(u.pos.x, u.pos.y);
      sphere.center.set(u.pos.x, gy + (u.jumpH ?? 0) + v.height / 2, -u.pos.y); // +glide: a flyer is culled where it flies
      sphere.radius = v.height + u.radius + 1.5;
      const onScreen = frustum.intersectsSphere(sphere);
      v.root.visible = onScreen;
      // Off-screen units leave the scene graph too: three.js walks (and re-multiplies the
      // matrices of) every object in the scene each frame, visible or not.
      if (onScreen) {
        if (v.root.parent !== scene) scene.add(v.root);
        const label = u.kind === 'player' ? 'r.units.hero' : 'r.units.mob';
        perf.begin(label);
        // +presence: a far other player animates every other frame (its root still follows the position)
        // and drops its outline hulls.
        const rh = u.remote ? remoteHulls.get(u.id) : undefined;
        if (rh) {
          const want = settings.outlines && u.remote !== 2;
          for (const h of rh) if (h.visible !== want) h.visible = want;
        }
        if (u.remote === 2 && (remoteFrame + u.id.length) & 1) {
          remoteDt.set(u.id, (remoteDt.get(u.id) ?? 0) + dtS);
          v.root.position.set(u.pos.x, gy + (u.jumpH ?? 0), -u.pos.y);
        } else {
          const skipped = u.remote ? (remoteDt.get(u.id) ?? 0) : 0;
          if (skipped) remoteDt.delete(u.id);
          v.update(u, dtS + skipped, frame.timeMs);
        }
        perf.end(label);
        // +field: elites stand larger; fresh field spawns rise out of the ground.
        let sc = u.elite ? ELITE_SCALE : 1;
        const rt = rising.get(u.id);
        if (rt !== undefined) {
          const t = rt + dtS;
          if (t >= RISE_S) rising.delete(u.id);
          else rising.set(u.id, t);
          const k = Math.min(1, t / RISE_S);
          const e = 1 - (1 - k) ** 3;
          v.root.position.y -= (1 - e) * v.height * 0.55;
          sc *= 0.55 + 0.45 * e + Math.sin(k * Math.PI) * 0.08;
        }
        if (v.root.scale.x !== sc) v.root.scale.setScalar(sc);
      } else {
        if (v.root.parent === scene) scene.remove(v.root);
        v.root.position.set(u.pos.x, gy, -u.pos.y);
      }
    }
    for (const [id, v] of units) {
      if (!byId.has(id)) {
        units.delete(id);
        const ns = remoteShadow.get(id); // +presence: the pooled VRM casts shadows again for its next user
        if (ns) for (const m of ns) m.castShadow = true;
        remoteShadow.delete(id);
        remoteDt.delete(id);
        for (const h of remoteHulls.get(id) ?? []) h.visible = settings.outlines;
        remoteHulls.delete(id);
        rising.delete(id);
        // +field: despawned corpses sink away (on screen); off screen they just go.
        if (v.root.visible && v.root.parent === scene) {
          dying.push({ v, t: 0 });
          stepDir.set(1, 0, 0);
          fx.clipCue('dust', v.root.position, stepDir, DUST_RGB, 0.6, false);
        } else v.dispose();
      }
    }
    for (let i = dying.length - 1; i >= 0; i--) {
      const d = dying[i]!;
      d.t += dtS;
      const k = Math.min(1, d.t / SINK_S);
      d.v.root.position.y -= dtS * (0.4 + 1.6 * k) * Math.max(0.6, d.v.height * 0.5);
      d.v.root.scale.multiplyScalar(1 - dtS * 0.5);
      if (k >= 1) {
        d.v.dispose();
        dying.splice(i, 1);
      }
    }
    // +field: elite rings (gold, slowly turning) under elite mobs.
    for (const u of frame.units) {
      const v = units.get(u.id);
      let ring = eliteRings.get(u.id);
      if (!u.elite || !v || !u.alive) {
        if (ring) {
          ring.removeFromParent();
          eliteRings.delete(u.id);
        }
        continue;
      }
      if (!ring) {
        ring = new THREE.Mesh(eliteGeo, eliteMat);
        ring.add(new THREE.Mesh(eliteInner, eliteMat));
        ring.renderOrder = 4;
        eliteRings.set(u.id, ring);
        scene.add(ring);
      }
      ring.visible = v.root.visible;
      const r = Math.max(1.1, u.radius * 1.35) * ELITE_SCALE;
      ring.position.set(v.root.position.x, groundY(u.pos.x, u.pos.y) + 0.06, v.root.position.z);
      ring.scale.setScalar(r * (1 + 0.04 * Math.sin(frame.timeMs / 300)));
      ring.rotation.y = frame.timeMs / 2400;
    }
    for (const [id, ring] of eliteRings)
      if (!byId.has(id)) {
        ring.removeFromParent();
        eliteRings.delete(id);
      }
    shared.beginBlobs();
    for (const v of units.values()) if (v.root.visible && v.blobR > 0) shared.addBlob(v.root.position.x, v.root.position.z, v.blobR, v.root.position.y);
    shared.endBlobs();
    const cues = frame.cues;
    // +impact: hit reacts come from the feel director when the hit's visual lands (sized by the hit).
    if (cues) for (let i = 0; i < cues.length; i++) if (cues[i]!.kind !== 'hit') units.get(cues[i]!.unitId)?.cue(cues[i]!.kind, cues[i]!.abilityId);
    return byId;
  }

  function syncAreas(frame: RenderFrame): void {
    const seen = new Set<string>();
    for (const a of frame.areas) {
      seen.add(a.id);
      let v = areas.get(a.id);
      if (!v) {
        v = new AreaDecal(a);
        areas.set(a.id, v);
        scene.add(v.group);
      }
      v.update(a);
    }
    for (const [id, v] of areas) {
      if (!seen.has(id)) {
        v.dispose();
        areas.delete(id);
      }
    }
  }

  function placeRing(ring: THREE.Mesh, u: RenderUnit | undefined, hostile: boolean, timeMs: number): void {
    if (!u) {
      ring.visible = false;
      return;
    }
    ring.visible = true;
    const r = Math.max(0.95, u.radius * 1.15) + 0.25;
    let pulse = 1;
    let flash = 0;
    if (ring === selRing) {
      const k = 1 - Math.min(1, selAge / SEL_PULSE_S);
      flash = k * k;
      pulse = 1 + Math.sin(timeMs / 260) * 0.03 + 0.4 * flash;
    }
    ring.scale.set(r * pulse, 1, r * pulse);
    ring.position.set(u.pos.x, groundY(u.pos.x, u.pos.y) + 0.045, -u.pos.y);
    const base = hostile ? FOE_RING : FRIEND_RING;
    const k = Math.min(1.2, 0.5 + 0.5 * settings.fxIntensity);
    (ring.material as THREE.MeshBasicMaterial).color.copy(base).multiplyScalar(k);
    if (ring === selRing) selEdgeMat.color.copy(base).multiplyScalar(k * (1.15 + 1.2 * flash));
  }

  /** Theme mood: background, hemisphere, key tint, fog colour and grade lift. */
  function applyTheme(t: DungeonTheme): void {
    const L = t.lighting;
    scene.background = new THREE.Color(L.background);
    hemi.color.setHex(L.hemiSky);
    hemi.groundColor.setHex(L.hemiGround);
    hemi.intensity = L.hemiIntensity;
    key.color.setHex(L.key);
    U.uFogColor.value.setRGB(L.fog[0], L.fog[1], L.fog[2]);
    grade.uLift.value.set(L.lift[0], L.lift[1], L.lift[2]);
    // +field: outdoor themes push the fog out and light the scene harder (defaults = dungeons).
    U.uFogNear.value = L.fogNear ?? 48;
    U.uFogFar.value = L.fogFar ?? 120;
    U.uFogAmt.value = L.fogAmt ?? 0.05;
    // +scenery: aerial perspective and grade per theme (defaults = the dungeon look).
    const fs = L.fogShape ?? [0.9, 1, 0, 0];
    U.uFogShape.value.set(fs[0], fs[1], fs[2], fs[3]);
    const ft = L.fogTint ?? [1, 1, 1];
    U.uFogTint.value.setRGB(ft[0], ft[1], ft[2]);
    grade.uSat.value = L.sat ?? 1.12;
    const gn = L.gain ?? [1.03, 1.0, 0.97];
    grade.uGain.value.set(gn[0], gn[1], gn[2]);
    key.intensity = L.keyIntensity ?? 3.2;
    U.uKeyIntensity.value = key.intensity / Math.PI;
  }

  /** Ray → ground point on an elevated floor (ground.ts rayToGround). */
  function pickOnGround(ray: THREE.Ray): Vec2 | null {
    return rayToGround(ray, camGround);
  }

  /** +glide: wind streaks round the followed hero, the glide wind and the nearest chute's hum. */
  function updateGlide(frame: RenderFrame, byId: Map<UnitId, RenderUnit>, dtS: number): void {
    const me = frame.playerId ? byId.get(frame.playerId) : undefined;
    const v = me ? units.get(me.id) : undefined;
    const flying = !!me && me.z !== undefined && !me.perched && (me.jumpH ?? 0) > 0.3;
    let speed = 0;
    let sink = 0;
    if (me && v && flying && dtS > 0 && Number.isFinite(lastFlight.x)) {
      glideVel.set((me.pos.x - lastFlight.x) / dtS, (me.z! - lastFlight.z) / dtS, -(me.pos.y - lastFlight.y) / dtS);
      if (glideVel.length() > 60) glideVel.set(0, 0, 0); // a snap, not a flight
      speed = Math.hypot(glideVel.x, glideVel.z);
      sink = -glideVel.y;
    } else glideVel.set(0, 0, 0);
    if (me && flying) {
      lastFlight.x = me.pos.x;
      lastFlight.y = me.pos.y;
      lastFlight.z = me.z!;
    } else lastFlight.x = NaN;
    if (v) glideAt.copy(v.root.position).setY(v.root.position.y + v.height * 0.6);
    windStreaks.update(dtS, v && flying ? glideAt : null, glideVel, flying && (!!me?.glide || sink > 8), camera);
    const near = airspace?.updrafts?.length ? nearestChute(airspace.updrafts, rig0.focus.x, rig0.focus.y) : null;
    let pan = 0;
    if (near) {
      projV.set(near.c.pos.x, groundY(near.c.pos.x, near.c.pos.y) + 4, -near.c.pos.y).project(camera);
      pan = Math.max(-1, Math.min(1, projV.x * 0.75));
    }
    glideAudio.update(windLevel(!!me?.glide, speed, sink, flying), { gain: near ? chuteLevel(near.d, near.c.r) : 0, pan });
  }
  const rig0 = { focus: { x: 0, y: 0 } };

  /** +camera: pivot, collided eye, floor clearance, look-at and FOV for this frame. */
  function placeCamera(rig: CameraRig, dtMs: number): void {
    const { yaw, pitch, focus } = rig.state;
    const distance = rig.state.distance * (1 + GLIDE_CAM_PULL * camGlide); // +glide: see where you'll land
    const ph = rig.pivotHeight() + camGround + camJump; // +jump
    const cp = Math.cos(pitch);
    // Unit vector pivot → eye (sim x / y, height).
    const ux = -Math.cos(yaw) * cp;
    const uy = -Math.sin(yaw) * cp;
    const uh = Math.sin(pitch);
    let open = distance;
    if (camCol && camCol.size) {
      const reach = distance + CAM_CLEAR;
      const ex = focus.x + ux * reach;
      const ey = focus.y + uy * reach;
      const eh = ph + uh * reach;
      // Centre ray plus two rays to the eye's sides (camera right = yaw - 90°).
      const sx = Math.sin(yaw) * CAM_SIDE;
      const sy = -Math.cos(yaw) * CAM_SIDE;
      const t = Math.min(camCol.cast(focus.x, focus.y, ph, ex, ey, eh), camCol.cast(focus.x, focus.y, ph, ex + sx, ey + sy, eh), camCol.cast(focus.x, focus.y, ph, ex - sx, ey - sy, eh));
      if (t < 1) open = Math.max(0.3, Math.min(distance, t * reach - CAM_CLEAR));
    }
    // +atmos: WoW slide: where the boom would dip into the ground (a low pitch, looking out or up; a
    // hill behind the hero) the eye slides in along it instead (snap in, ease back out like walls).
    open = Math.min(open, Math.max(0.3, boomGroundLimit(groundY, focus.x, focus.y, ph, ux, uy, uh, distance, EYE_FLOOR_CLEARANCE)));
    camDist = Math.min(distance, collideDistance(camDist, open, dtMs));
    const ex = focus.x + ux * camDist;
    const ey = focus.y + uy * camDist;
    let eh = ph + uh * camDist;
    const floor = groundY(ex, ey) + 0.35;
    if (eh < floor) eh = floor;
    camera.position.set(ex, eh, -ey);
    camPivot.set(focus.x, ph, -focus.y);
    camera.lookAt(camPivot);
    const fov = rig.fov();
    if (Math.abs(fov - baseFov) > 0.01) {
      baseFov = fov;
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
    camera.updateMatrixWorld();
  }

  /** +basekits: opacity of a stealthed hero (Lurk). */
  const STEALTH_ALPHA = 0.35;
  /** +camera: fade heroes the camera is inside / that stand between it and the pivot. */
  function updateSelfFade(frame: RenderFrame, dtS: number): void {
    const cx = camera.position.x;
    const cy = camera.position.y;
    const cz = camera.position.z;
    for (const u of frame.units) {
      if (u.kind !== 'player') continue;
      const v = units.get(u.id);
      if (!v || !v.root.visible) continue;
      const p = v.root.position;
      const d = capsuleDistance(cx, cy, cz, p.x, p.z, p.y, p.y + v.height);
      const blocking = u.id !== frame.playerId && blocksView(cx, cy, cz, camPivot.x, camPivot.y, camPivot.z, p.x, p.y + v.height * 0.6, p.z);
      const a = u.stealth ? Math.min(STEALTH_ALPHA, fadeTarget(d, blocking)) : fadeTarget(d, blocking); // +basekits: Lurk
      if (a < 0.999) selfFade.want(u.id, v.root, v.hitbox, a);
    }
    selfFade.update(dtS);
  }

  function applySettings(s: GraphicsSettings): void {
    const prevQuality = settings.quality;
    settings = { ...s };
    profile = qualityProfile(settings.quality);
    setShadowHue(settings.shadowHue);
    atmosphere.mode = settings.timeOfDay; // +atmos
    atmosphere.setQuality(settings.quality); // +atmos: far LOD reach, light shafts
    U.uFx.value = settings.fxIntensity;
    shared.fx = settings.fxIntensity;
    fx.intensity = settings.fxIntensity;
    mechFx.intensity = settings.fxIntensity;
    fx.shakeEnabled = settings.screenShake;
    feel.intensity = settings.fxIntensity; // +impact
    feel.cameraEnabled = settings.screenShake; // +impact
    afterimages.intensity = settings.fxIntensity; // +impact
    dungeon?.setFloorBrightness(settings.floorBrightness);
    dungeon?.setDetail(settings.quality !== 'low');
    unitCtx.mobShadows = profile.mobShadows;
    unitCtx.springHz = profile.springHz;
    if (prevQuality !== settings.quality) {
      gl.setPixelRatio(Math.min(window.devicePixelRatio || 1, profile.pixelRatioCap, 2));
      if (gl.shadowMap.enabled !== profile.shadows) {
        gl.shadowMap.enabled = profile.shadows;
        scene.traverse((o) => {
          const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
          if (m) (Array.isArray(m) ? m : [m]).forEach((x) => (x.needsUpdate = true));
        });
      }
      if (key.shadow.mapSize.x !== profile.shadowMapSize) {
        key.shadow.mapSize.set(profile.shadowMapSize, profile.shadowMapSize);
        key.shadow.map?.dispose();
        key.shadow.map = null as unknown as THREE.WebGLRenderTarget;
      }
      // New render targets (MSAA samples / pixel ratio) only: the passes and their compiled
      // shaders stay, so a live quality change (auto-scaler) doesn't hitch on recompiles.
      post.setMsaa(profile.msaa);
      post.setSize(size.w, size.h, gl.getPixelRatio());
    }
    post.bloomEnabled = settings.bloom && profile.bloomAllowed;
    applyOutlineVisibility();
    selfFade.outlines = settings.outlines; // +camera
  }
  applySettings(settings);

  return {
    combatTextEvents() {
      return fx.textEvents();
    },

    init(d: DungeonDef | undefined) {
      dungeon?.dispose();
      dungeon = null;
      townNpcs?.dispose(); // +field
      townNpcs = null;
      gatherNodes?.dispose(); // +prof
      gatherNodes = null;
      skyways?.dispose(); // +glide
      skyways = null;
      airspace = d?.airspace ?? null;
      glideAudio.silence();
      celebrations.clear();
      rising.clear();
      for (const dd of dying) dd.v.dispose();
      dying.length = 0;
      for (const r of eliteRings.values()) r.removeFromParent();
      eliteRings.clear();
      sinceInit = 0;
      if (d) {
        // Elevation first: the dressing, doors, units and fx all read the ground height.
        setGroundForDungeon(d);
        dungeon = buildToonDungeon(d, assets, hulls, gl.capabilities.getMaxAnisotropy());
        stabilizePrograms(dungeon.group);
        dungeon.setFloorBrightness(settings.floorBrightness);
        dungeon.setDetail(settings.quality !== 'low');
        applyTheme(dungeon.theme);
        scene.add(dungeon.group);
        // +atmos: open maps draw the whole zone and the ranges around it.
        camera.far = (dungeon as Partial<FieldBuild>).layout ? OUTDOOR_FAR : 400;
        camera.updateProjectionMatrix();
        const theme = dungeon.theme; // +audio
        const field = (dungeon as Partial<FieldBuild>).layout ? (dungeon as FieldBuild) : null; // +field
        if (field && d.field) {
          // +field: outdoor beds (wind, hearth, birds), cobble / grass footsteps, the barrow hum.
          const L = field.layout;
          ambience.setField(d.field.theme, null, field.hums, d, { plaza: (x, y) => (L.plaza.length ? samplePlaza(L, x, y) : 0), path: (x, y) => (L.path.length ? sampleLayer(L, L.path, x, y) : 0) }); // +audio: area beds, water, surfaces
          music.play('mossvale'); // +music
          if (d.field.gatherNodes?.length) {
            // +prof: herb and ore nodes (instanced, outlined) with their sparkles.
            registerGatherClips();
            gatherNodes = new GatherNodesView(d.field.gatherNodes, groundY, hulls);
            gatherNodes.setTracked(gatherTracking);
            stabilizePrograms(gatherNodes.group);
            scene.add(gatherNodes.group);
          }
          // +glide: wind chutes and the sky puzzles' platforms.
          if (d.airspace) {
            skyways = buildSkyways(d.airspace, groundY);
            if (skyways) {
              stabilizePrograms(skyways.group);
              scene.add(skyways.group);
            }
          }
          if (d.field.npcs?.length) {
            townNpcs = new TownNpcs(d.field.npcs, assets, hulls, groundY, profile.mobShadows);
            townNpcs.setMarkers(npcMarkers);
            stabilizePrograms(townNpcs.group);
            scene.add(townNpcs.group);
          }
        } else {
          ambience.setDungeon(d, (r) => theme.room(r, d), d.corridors[0] ? theme.corridorFloor(d.corridors[0], d) : undefined); // +audio
          music.play(null); // +music: dungeons keep their room tones
        }
        worldLoot.init(d); // +world-resources
        camCol = new CamCollision(dungeon.cameraBlockers()); // +camera: registered blockers (dungeon.ts)
        camDist = NaN; // +camera
        feel.clear(); // +impact
        afterimages.clear(); // +impact
        if (import.meta.env.DEV) Object.assign(globalThis as object, { __toonDungeon: dungeon });
        camGround = NaN;
        camJump = 0;
        console.info(`[toon] dungeon (${dungeon.theme.id}): ${dungeon.counts.walls} wall pieces, ${dungeon.counts.props} props, ${dungeon.counts.torches} flames, ${dungeon.counts.doors} doors, ${dungeon.counts.clusters} clusters, ${dungeon.counts.drawables} static draws (all clusters)`);
      } else {
        setGroundForDungeon(undefined);
        ambience.setHub(); // +audio
        music.play(null); // +music
        camCol = null; // +camera
        worldLoot.init(undefined); // +world-resources
        applyTheme(GENERIC_THEME);
      }
      applyOutlineVisibility();
    },

    render(frame: RenderFrame, rig: CameraRig, dtMs: number) {
      if (disposed) return;
      const t0 = performance.now();
      lastFrame = frame;
      const dtS = Math.min(0.1, Math.max(0, dtMs / 1000));
      U.uTime.value += dtS;
      fpsAcc += dtMs;
      fpsFrames++;
      if (fpsAcc > 1000) {
        fps = (fpsFrames * 1000) / fpsAcc;
        fpsAcc = 0;
        fpsFrames = 0;
      }

      // Camera: the rig is 2D; the camera rides the floor height under the focus, smoothed so
      // stairs and ledges don't jolt it. +camera: the pivot rises toward the hero's head as it
      // zooms in, the boom collides with walls / posts / big props, the FOV widens close in.
      camGround = smoothHeight(camGround, groundY(rig.state.focus.x, rig.state.focus.y), dtMs);
      // +jump: follow the hero's rendered hop height (the same interpolated offset the model uses),
      // vertical only, ~70 ms lag so the arc feels anchored; it settles back without a jolt on landing.
      {
        let jh = 0;
        let gliding = false;
        let flying = false;
        // +glide: a flyer's height is absolute: the pivot rides it over the ground under the focus.
        for (const fu of frame.units) {
          if (fu.z !== undefined) fu.jumpH = Math.max(0, fu.z - groundY(fu.pos.x, fu.pos.y));
          if (fu.id !== frame.playerId) continue;
          jh = fu.z !== undefined ? Math.max(0, fu.z - camGround) : (fu.jumpH ?? 0);
          gliding = !!fu.glide;
          flying = fu.z !== undefined;
        }
        camJump += (jh - camJump) * (1 - Math.exp(-dtMs / (flying ? GLIDE_CAM_TAU_MS : JUMP_CAM_TAU_MS)));
        if (Math.abs(camJump) < 1e-4) camJump = 0;
        camGlide += ((gliding ? 1 : 0) - camGlide) * (1 - Math.exp(-dtMs / GLIDE_CAM_EASE_MS));
        if (camGlide < 1e-3) camGlide = 0;
      }
      const headOf = frame.playerId ? units.get(frame.playerId) : undefined; // +camera
      if (headOf) rig.headHeight = headOf.height * HEAD_FRAC; // +camera
      rig0.focus = rig.state.focus; // +glide
      placeCamera(rig, dtMs); // +camera
      atmosphere.apply({ key, hemi, grade, keyDir: KEY_DIR }); // +atmos: time of day (the capture hook below may override it)
      if (captureHook.camera) baseFov = captureHook.camera(camera, rig, groundY, scene, { key, hemi, grade, keyDir: KEY_DIR }); // +capture: scripted trailer camera (?capture=1 only)
      dungeon?.cutaway(camera.position, rig.state.focus, camGround); // +dungeon2: enclosed-room cutaway
      (dungeon as Partial<FieldBuild> | null)?.frame?.(camera, frame.playerId ? (frame.units.find((u) => u.id === frame.playerId)?.pos ?? null) : null); // +field: sky, portal labels
      sinceInit += dtS;

      // Doors / seals: sim events in, quick animations out.
      if (dungeon) {
        dungeon.ingest(frame.events);
        dungeon.update(dtS);
      }

      // Shadow frustum follows the focus point.
      const f = rig.state.focus;
      key.target.position.set(f.x, camGround, -f.y);
      key.position.copy(key.target.position).addScaledVector(KEY_DIR, 50);
      key.target.updateMatrixWorld();

      // Hit-stop: big crits briefly slow animation and effects (never the sim).
      const fxDt = fx.beginFrame(dtS);
      combatAudio.beginFrame(); // +impact
      fxView.playerId = frame.playerId;
      perf.begin('r.units');
      const byId = syncUnits(frame, fxDt);
      if (frame.phantoms) for (const ph of frame.phantoms) byId.set(ph.id, ph); // +presence: other players' cast targets (fx aim only)
      updateSelfFade(frame, dtS); // +camera
      perf.end('r.units');
      perf.begin('r.trails');
      shared.trails.update(fxDt, settings.fxIntensity);
      perf.end('r.trails');
      perf.begin('r.fx');
      syncAreas(frame);
      mechFx.update(frame.mechanics, byId, unitHeight, fxDt);
      fxView.units = byId;
      fxView.playerId = frame.playerId;
      fxView.focus = rig.state.focus;
      fx.ingest(frame, fxView);
      feel.ingest(frame, fxView); // +impact
      // +field: level-ups (everyone sees them), field spawns rising in.
      if (frame.events)
        for (const e of frame.events) {
          if (e.type === 'level_up') celebrateUnit(e.sourceID, levelUpSize(e.gained), true);
          else if (e.type === 'waystone') {
            // +presence: a waystone jump (the hero's own, or another player's 'travel'): a pale poof where it left and where it lands.
            const mine = e.sourceID === frame.playerId;
            waystonePoof(e.from.x, e.from.y, mine);
            waystonePoof(e.to.x, e.to.y, mine);
          } else if (e.type === 'glide' && e.on) {
            // +glide: the canopy's "fwump": a pale cloth puff where it unfurls.
            const v = units.get(e.sourceID);
            if (v) {
              poofAt.copy(v.root.position);
              poofAt.y += v.height + 1.4;
              fx.clipCue('dust', poofAt, stepDir, CLOTH_RGB, 1.0, e.sourceID === frame.playerId);
            }
          } else if (e.type === 'land' && (e.drop ?? 0) > 1.6) {
            // +glide: dust on a landing from a real drop (bigger from higher).
            const v = units.get(e.sourceID);
            if (v) fx.clipCue('dust', v.root.position, stepDir, DUST_RGB, 0.6 + Math.min(0.7, (e.drop ?? 0) / 25), e.sourceID === frame.playerId);
          } else if (e.type === 'field_spawn' && sinceInit > 1.5) {
            rising.set(e.sourceID, 0);
            const v = units.get(e.sourceID);
            if (v) {
              stepDir.set(1, 0, 0);
              fx.clipCue('dust', v.root.position, stepDir, DUST_RGB, 0.5, false);
            }
          }
        }
      celebrations.intensity = settings.fxIntensity;
      celebrations.update(
        fxDt,
        (id) => {
          const v = units.get(id);
          return v ? followV.copy(v.root.position) : null;
        },
        camera,
        size.h,
      );
      townNpcs?.update(dtS, camera, frustum, frame.playerId ? (byId.get(frame.playerId)?.pos ?? null) : null);
      gatherNodes?.update(frame.gather, frame.timeMs); // +prof
      fx.update(fxDt, fxView);
      feel.update(dtS, fxView); // +impact
      footsteps.flush(camera, rig.state.focus); // +controls
      ambience.update(rig.state.focus); // +audio: room tone follows the camera
      afterimages.update(dtS); // +impact
      worldLoot.update(frame, fxDt, byId, settings.fxIntensity); // +world-resources
      updateGlide(frame, byId, dtS); // +glide
      perf.end('r.fx');

      const player = frame.playerId ? byId.get(frame.playerId) : undefined;
      const sel = frame.selectedId ? byId.get(frame.selectedId) : undefined;
      const hov = frame.hoverId && frame.hoverId !== frame.selectedId ? byId.get(frame.hoverId) : undefined;
      const hostile = (u: RenderUnit | undefined): boolean => !!u && u.faction !== (player?.faction ?? 'party');
      if (frame.selectedId !== selId) {
        selId = frame.selectedId;
        selAge = 0;
      } else selAge += dtS;
      placeRing(selRing, sel, hostile(sel), frame.timeMs);
      placeRing(hovRing, hov, hostile(hov), frame.timeMs);
      groundCues.update(frame, camera, dtS, settings.fxIntensity); // +castux
      marker.visible = !!sel && sel.alive;
      if (sel) {
        const h = units.get(sel.id)?.height ?? 2;
        marker.position.set(sel.pos.x, groundY(sel.pos.x, sel.pos.y) + h + 0.75 + Math.sin(frame.timeMs / 220) * 0.12, -sel.pos.y);
        marker.rotation.y += dtS * 2;
        markerMat.color.copy(hostile(sel) ? FOE_RING : FRIEND_RING).multiplyScalar(0.7);
      }

      // Light screen shake: offset the camera for this render only (picking stays stable).
      fx.shakeOffset(shakeOff);
      // +impact: plus the directional kick along the blow, and the FOV punch.
      const kick = feel.kick;
      shakeOff.x += kick.x;
      shakeOff.y += kick.y;
      const shaking = shakeOff.x !== 0 || shakeOff.y !== 0;
      if (shaking) {
        camSaved.copy(camera.position);
        camRight.setFromMatrixColumn(camera.matrixWorld, 0);
        camUp.setFromMatrixColumn(camera.matrixWorld, 1);
        camera.position.addScaledVector(camRight, shakeOff.x).addScaledVector(camUp, shakeOff.y);
        camera.updateMatrixWorld();
      }
      // +field: the level-up FOV punch rides on the hit kick (presentation only, motion settings respected).
      const lvlFov = shakeAllowed() ? celebrations.fov : 0;
      const punching = kick.fov !== 0 || lvlFov !== 0;
      if (punching) {
        camera.fov = baseFov - kick.fov - lvlFov; // +camera: base FOV follows the zoom
        camera.updateProjectionMatrix();
      }
      // +field: the ding's flash through the post chain (exposure + bloom), milder with reduced motion.
      const flashK = reducedMotion ? 0.5 : 1;
      gl.toneMappingExposure = 1 + celebrations.exposure * flashK;
      bloom.strength = baseBloom + celebrations.bloom * flashK;
      grade.uPunch.value = feel.punchFrame;
      grade.uHurt.value = feel.hurt * Math.min(1, 0.4 + 0.6 * settings.fxIntensity);
      gl.info.reset();
      // Debug handles for live A/B experiments (?debug=perf only).
      if (perf.enabled && (perf.handles['gl'] !== gl || perf.handles['dungeon'] !== dungeon)) perf.handles = { gl, scene, camera, post, bloom, key, units, dungeon };
      perf.begin('r.gl');
      post.render();
      perf.end('r.gl');
      perf.renderer = perf.enabled ? { drawCalls: gl.info.render.calls, triangles: gl.info.render.triangles, programs: gl.info.programs?.length ?? 0, textures: gl.info.memory.textures, geometries: gl.info.memory.geometries } : null;
      if (shaking) {
        camera.position.copy(camSaved);
        camera.updateMatrixWorld();
      }
      if (punching) {
        camera.fov = baseFov; // +camera
        camera.updateProjectionMatrix();
      }
      cpuMs = cpuMs * 0.9 + (performance.now() - t0) * 0.1;
    },

    prewarm() {
      if (disposed) return;
      const t0 = performance.now();
      const before = gl.info.programs?.length ?? 0;
      // Off-screen units are hidden until they first scroll into view; area decals (telegraphs,
      // zones) and their shader only exist once the first one appears. Show / create all of them
      // for one compile + texture upload + draw, then put everything back.
      const shown: THREE.Object3D[] = [];
      for (const v of units.values()) {
        if (!v.root.visible) {
          v.root.visible = true;
          if (v.root.parent !== scene) scene.add(v.root);
          shown.push(v.root);
        }
      }
      // Far off-screen: compile() ignores the frustum, the visible draw below culls them.
      const far = { x: fxView.focus.x + 5000, y: fxView.focus.y + 5000 };
      const dummies = (['telegraph', 'zone'] as const).flatMap((kind) =>
        [true, false].map((hostile) => new AreaDecal({ id: `__warm_${kind}_${hostile}`, kind, hostile, progress: 0.5, area: { shape: { type: 'circle', radius: 2 }, origin: far, facing: 0 } })),
      );
      for (const d of dummies) scene.add(d.group);
      const textures = new Set<THREE.Texture>();
      scene.traverse((o) => {
        const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (!m) return;
        for (const mat of Array.isArray(m) ? m : [m]) {
          for (const v of Object.values(mat)) if (v instanceof THREE.Texture) textures.add(v);
          const uniforms = (mat as THREE.ShaderMaterial).uniforms;
          if (uniforms) for (const u of Object.values(uniforms)) if (u?.value instanceof THREE.Texture) textures.add(u.value);
        }
      });
      // +field: a celebration far off-screen so the level-up shaders compile now, not at the ding.
      celebrateAt.set(far.x, 0, -far.y);
      celebrations.spawn('big', null, celebrateAt, false);
      celebrations.update(0.5, () => null, camera, size.h);
      // Blade trails are hidden until the first swing: show them for the compile too.
      shared.trails.warm(true);
      mechFx.warm(true);
      groundCues.warm(true); // +castux
      try {
        gl.compile(scene, camera);
        for (const tex of textures) gl.initTexture(tex);
      } finally {
        shared.trails.warm(false);
        mechFx.warm(false);
        groundCues.warm(false); // +castux
        for (const o of shown) {
          o.visible = false;
          scene.remove(o);
        }
        for (const d of dummies) d.dispose();
        celebrations.clear(); // +field
      }
      // Redraw the real frame (post passes + shadow map warm, canvas left correct).
      post.render();
      console.info(`[toon] prewarm: ${(gl.info.programs?.length ?? 0) - before} programs compiled, ${textures.size} textures, ${shown.length} hidden units, ${(performance.now() - t0).toFixed(0)} ms`);
    },

    pickGround(sx, sy) {
      raycaster.setFromCamera(ndc(sx, sy), camera);
      return pickOnGround(raycaster.ray);
    },

    pickUnit(sx, sy) {
      raycaster.setFromCamera(ndc(sx, sy), camera);
      pickList.length = 0;
      for (const v of units.values()) if (v.root.visible) pickList.push(v.hitbox);
      const hits = raycaster.intersectObjects(pickList, false);
      const alive = new Set(lastFrame?.units.filter((u) => u.alive).map((u) => u.id) ?? []);
      const ids = hits.map((h) => h.object.userData['unitId'] as UnitId);
      return ids.find((id) => alive.has(id)) ?? ids[0] ?? null;
    },

    project(p: Vec2, height: number): ScreenPoint {
      // `height` is above the floor; the floor itself may be raised (elevation).
      const v = projectGround(p, height, camera, projV);
      const r = canvasRect();
      return {
        x: r.left + ((v.x + 1) / 2) * r.width,
        y: r.top + ((1 - v.y) / 2) * r.height,
        visible: v.z < 1 && v.z > -1 && Math.abs(v.x) <= 1.1 && Math.abs(v.y) <= 1.1,
      };
    },

    unitHeight: (id) => {
      const v = units.get(id);
      return v ? v.height + 0.25 : 2;
    },

    resize(w, h) {
      rect = null;
      size = { w: Math.max(1, w), h: Math.max(1, h) };
      gl.setSize(size.w, size.h);
      post.setSize(size.w, size.h, gl.getPixelRatio());
      bloom.resolution.set(size.w, size.h);
      camera.aspect = size.w / size.h;
      camera.updateProjectionMatrix();
    },

    applySettings,

    setNpcMarkers(m: Record<string, NpcMarker | null>) {
      npcMarkers = { ...m };
      townNpcs?.setMarkers(npcMarkers);
    },

    celebrate(unitId: UnitId, sz: 'small' | 'big') {
      celebrateUnit(unitId, sz, true);
    },

    setGatherTracking(prof: GatherProfession | null) {
      gatherTracking = prof; // +prof
      gatherNodes?.setTracked(prof);
    },

    npcEmote(npcId: string) {
      townNpcs?.emote(npcId); // +quests
    },

    npcAt(sx: number, sy: number): string | null {
      if (!townNpcs) return null;
      raycaster.setFromCamera(ndc(sx, sy), camera);
      return townNpcs.pick(raycaster);
    },

    stats() {
      return { fps, drawCalls: gl.info.render.calls, triangles: gl.info.render.triangles, units: units.size, visibleUnits: [...units.values()].filter((v) => v.root.visible).length, cpuMs, programs: gl.info.programs?.length ?? 0 };
    },

    dispose() {
      disposed = true;
      for (const v of units.values()) v.dispose();
      units.clear();
      for (const v of areas.values()) v.dispose();
      mechFx.dispose();
      worldLoot.dispose(); // +world-resources
      groundCues.dispose(); // +castux
      areas.clear();
      fx.dispose();
      afterimages.dispose(); // +impact
      townNpcs?.dispose(); // +field
      gatherNodes?.dispose(); // +prof
      skyways?.dispose(); // +glide
      windStreaks.dispose();
      glideAudio.silence();
      celebrations.dispose();
      for (const dd of dying) dd.v.dispose();
      for (const r of eliteRings.values()) r.removeFromParent();
      eliteGeo.dispose();
      eliteInner.dispose();
      eliteMat.dispose();
      ambience.setHub(); // +audio: back to the hub bed
      music.play(null); // +music
      selfFade.dispose(); // +camera
      dungeon?.dispose();
      dungeon = null;
      setGroundForDungeon(undefined);
      shared.dispose();
      ringGeo.dispose();
      edgeGeo.dispose();
      selEdgeMat.dispose();
      (selRing.material as THREE.Material).dispose();
      (hovRing.material as THREE.Material).dispose();
      markerGeo.dispose();
      markerMat.dispose();
      post.dispose();
      key.shadow.map?.dispose();
      gl.dispose();
      gl.forceContextLoss();
      gl.domElement.remove();
    },
  };
}

/** +field: town cobble weight at a sim point (footstep surface). */
function samplePlaza(L: FieldBuild['layout'], x: number, y: number): number {
  return sampleLayer(L, L.plaza, x, y);
}

/** +audio: a layout weight grid (plaza, roads) at a sim point, nearest cell. */
function sampleLayer(L: FieldBuild['layout'], f: Float32Array, x: number, y: number): number {
  const g = L.grid;
  const i = Math.round((x - g.x0) / g.cell);
  const j = Math.round((y - g.y0) / g.cell);
  if (i < 0 || j < 0 || i >= g.nx || j >= g.ny) return 0;
  return f[j * g.nx + i] ?? 0;
}
